import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { BusinessAside } from '../components/SettingsAsides';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageHeader, SavedNote, Section, Switch } from '../components/forms';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { useLocations } from '../lib/location';

type Business = {
  name: string;
  legalName: string | null;
  kvkNumber: string | null;
  vatNumber: string | null;
  kor: boolean;
  phone: string | null;
  email: string | null;
  address: { street: string | null; houseNumber: string | null; postcode: string | null; city: string | null };
};

export function BusinessPage() {
  const t = useT();
  const { orgId } = useParams({ from: '/s/$orgId/salon/bedrijf' });
  const qc = useQueryClient();
  const locations = useLocations(orgId);
  const data = useQuery({ queryKey: ['business', orgId], queryFn: () => api<Business>(`/orgs/${orgId}/business`) });
  const [form, setForm] = useState<Business | null>(null);
  const [saved, setSaved] = useState(false);
  const [lookupMsg, setLookupMsg] = useState<string | null>(null);
  useEffect(() => {
    if (data.data) setForm(data.data);
  }, [data.data]);

  const save = useMutation({
    mutationFn: (b: Business) => api<Business>(`/orgs/${orgId}/business`, { method: 'PUT', body: JSON.stringify(b) }),
    onSuccess: (b) => {
      qc.setQueryData(['business', orgId], b);
      qc.invalidateQueries({ queryKey: ['salon', orgId] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  async function lookup() {
    if (!form?.address.postcode || !form.address.houseNumber) return;
    setLookupMsg(null);
    const q = new URLSearchParams({ postcode: form.address.postcode, number: form.address.houseNumber });
    const res = await api<{ address: { street: string; city: string | null } | null }>(`/address?${q}`).catch(() => ({ address: null }));
    if (res.address) setForm({ ...form, address: { ...form.address, street: res.address.street, city: res.address.city } });
    else setLookupMsg(t('business.notFound'));
  }

  if (!form) return <p className="text-muted">{t('common.loading')}</p>;
  const set = <K extends keyof Business>(k: K, v: Business[K]) => setForm({ ...form, [k]: v });
  const setAddr = (k: keyof Business['address'], v: string) => setForm({ ...form, address: { ...form.address, [k]: v } });

  return (
    <SettingsPage orgId={orgId} aside={<BusinessAside form={form} />}>
      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <PageHeader orgId={orgId} title={t('business.title')} intro={t('business.intro')} />
        <Section title={t('business.title')}>
          <Field id="b-name" required label={t('business.name')} value={form.name} onChange={(e) => set('name', e.target.value)} />
          <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
            <Field id="b-postcode" label={t('business.postcode')} value={form.address.postcode ?? ''} onChange={(e) => setAddr('postcode', e.target.value)} onBlur={lookup} autoComplete="postal-code" />
            <Field id="b-number" label={t('business.houseNumber')} value={form.address.houseNumber ?? ''} onChange={(e) => setAddr('houseNumber', e.target.value)} onBlur={lookup} />
            <Button type="button" variant="secondary" onClick={lookup}>
              {t('business.lookup')}
            </Button>
          </div>
          {lookupMsg && <p className="text-sm text-muted">{lookupMsg}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="b-street" label={t('business.street')} value={form.address.street ?? ''} onChange={(e) => setAddr('street', e.target.value)} autoComplete="address-line1" />
            <Field id="b-city" label={t('business.city')} value={form.address.city ?? ''} onChange={(e) => setAddr('city', e.target.value)} autoComplete="address-level2" />
            <Field id="b-phone" type="tel" label={t('business.phone')} value={form.phone ?? ''} onChange={(e) => set('phone', e.target.value)} autoComplete="tel" />
            <Field id="b-email" type="email" label={t('business.email')} value={form.email ?? ''} onChange={(e) => set('email', e.target.value)} />
          </div>
        </Section>
        <Section title={t('business.legal')}>
          <Field id="b-legal" label={t('business.legalName')} value={form.legalName ?? ''} onChange={(e) => set('legalName', e.target.value)} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field id="b-kvk" inputMode="numeric" label={t('business.kvk')} value={form.kvkNumber ?? ''} onChange={(e) => set('kvkNumber', e.target.value)} />
            <Field id="b-vat" label={t('business.vat')} placeholder="NL123456789B01" value={form.vatNumber ?? ''} onChange={(e) => set('vatNumber', e.target.value)} />
          </div>
          <Switch label={t('business.kor')} hint={t('business.korHint')} checked={form.kor} onChange={(v) => set('kor', v)} />
        </Section>
        <ErrorNote message={save.error instanceof ApiError ? save.error.message : save.error ? t('error.generic') : null} />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={save.isPending}>
            {t('common.save')}
          </Button>
          <SavedNote show={saved} />
        </div>
        {locations.data?.length === 1 && (
          <Section title={t('loc.addSecond')} intro={t('loc.addSecondHint')}>
            <Link to="/s/$orgId/salon/locaties" params={{ orgId }} search={{ nieuw: 1 }} className="self-start text-sm font-semibold text-accent-strong hover:underline">
              + {t('loc.add')}
            </Link>
          </Section>
        )}
      </form>
    </SettingsPage>
  );
}
